// Commercial Energy Glossary — each term has its own page.
export const GLOSSARY = [
  {
    slug: "capacity",
    term: "Capacity",
    group: "Electricity",
    short: "The maximum amount of power the grid must be able to generate to meet peak demand.",
    definition:
      "Capacity is the total generating capability that must be available to reliably serve demand during the highest-usage periods of the year.",
    explanation:
      "Grid operators require enough generation resources to meet peak demand plus a reserve margin, even if those resources only run a few hours a year. Commercial customers ultimately pay for their share of this standby capability through capacity charges, which are typically allocated based on each customer's usage during a small number of annual peak hours.",
    whyItMatters:
      "Because capacity costs are tied to your usage during peak windows, reducing consumption during those specific hours can lower this component of your bill for a full delivery year.",
    related: { articles: ["understanding-capacity-charges", "understanding-commercial-energy-markets"], services: ["commercial-electricity"] },
  },
  {
    slug: "capacity-charges",
    term: "Capacity Charges",
    group: "Electricity",
    short: "The portion of your bill that pays for generation kept on standby to meet peak demand.",
    definition:
      "Capacity charges recover the cost of ensuring enough generation is available to meet system peak demand and maintain grid reliability.",
    explanation:
      "In many markets, your capacity charge is set by your contribution to the grid's peak — often measured during a handful of the highest-demand hours in the prior year. That figure, called an Installed Capacity or capacity tag, can apply to your account for the following year, making peak periods disproportionately important.",
    whyItMatters:
      "Capacity can represent a meaningful share of the all-in cost of electricity. Managing usage during peak-setting hours (peak shaving) can reduce your capacity tag and lower costs.",
    related: { articles: ["understanding-capacity-charges", "understanding-demand-charges"], services: ["commercial-electricity"] },
  },
  {
    slug: "demand",
    term: "Demand",
    group: "Electricity",
    short: "The rate at which your facility draws electricity at a given moment, measured in kilowatts (kW).",
    definition:
      "Demand is the instantaneous or short-interval rate of electricity consumption, as opposed to total energy consumed over time.",
    explanation:
      "If energy (kWh) is the total volume of electricity you use, demand (kW) is how fast you use it. Utilities record demand in short intervals (often 15 minutes) and typically bill based on your highest interval during the billing period.",
    whyItMatters:
      "Two facilities can use the same total energy yet pay very different amounts if one has sharp demand spikes. Understanding demand is the first step to controlling demand charges.",
    related: { articles: ["understanding-demand-charges", "understanding-commercial-utility-bills"], services: ["commercial-electricity"] },
  },
  {
    slug: "demand-charges",
    term: "Demand Charges",
    group: "Electricity",
    short: "Charges based on your highest sustained power draw during a billing period.",
    definition:
      "Demand charges bill you for the peak rate of consumption (in kW) recorded during the period, reflecting the capacity your service must be able to deliver.",
    explanation:
      "Because the grid and utility infrastructure must be sized for your maximum draw, demand charges recover the cost of that capacity. A single spike — such as starting large equipment simultaneously — can set your demand charge for the entire month.",
    whyItMatters:
      "For many commercial accounts, demand charges are a large share of the bill. Staggering equipment startup, load shifting, and on-site storage can all reduce them.",
    related: { articles: ["understanding-demand-charges", "understanding-load-factor"], services: ["commercial-electricity"] },
  },
  {
    slug: "distribution",
    term: "Distribution",
    group: "Delivery",
    short: "The local network of lower-voltage lines that delivers energy to your building.",
    definition:
      "Distribution refers to the local poles, wires, transformers, and pipes that carry energy the final stretch from the transmission system to end users.",
    explanation:
      "Distribution is operated by your regulated local utility even in deregulated markets. Distribution (or delivery) charges are regulated and appear on your bill regardless of which supplier you choose for the commodity itself.",
    whyItMatters:
      "Because distribution is not competitive, procurement focuses on the supply (commodity) portion of your bill — but understanding delivery charges helps you see the full picture.",
    related: { articles: ["understanding-commercial-utility-bills", "utility-vs-energy-supplier"], services: ["commercial-electricity", "commercial-natural-gas"] },
  },
  {
    slug: "transmission",
    term: "Transmission",
    group: "Delivery",
    short: "The high-voltage network that moves electricity long distances across a region.",
    definition:
      "Transmission is the system of high-voltage lines and substations that carries power from generation sources to local distribution networks.",
    explanation:
      "Transmission charges recover the cost of this regional backbone. Like capacity, they are often tied to your demand during specific system peak intervals, so peak management can influence them.",
    whyItMatters:
      "Transmission is a distinct cost component that can be influenced by when you use power, not just how much — an often-overlooked opportunity for large accounts.",
    related: { articles: ["understanding-capacity-charges", "understanding-commercial-energy-markets"], services: ["commercial-electricity"] },
  },
  {
    slug: "utility",
    term: "Utility",
    group: "General",
    short: "The regulated company that owns the wires/pipes and delivers energy to your facility.",
    definition:
      "A utility is the regulated entity responsible for delivering energy over its infrastructure and maintaining reliable service in its territory.",
    explanation:
      "In deregulated markets, the utility still handles delivery, metering, billing, and emergencies — but you can choose a separate competitive supplier for the energy itself. The utility does not profit from which supplier you select.",
    whyItMatters:
      "Knowing the difference between your utility and your supplier is fundamental to understanding your bill and your procurement options.",
    related: { articles: ["utility-vs-energy-supplier", "what-is-energy-deregulation"], services: ["commercial-electricity", "commercial-natural-gas"] },
  },
  {
    slug: "supplier",
    term: "Supplier",
    group: "General",
    short: "The competitive company that sells you the energy commodity in a deregulated market.",
    definition:
      "A supplier (or retail energy provider) is the company from which you purchase the electricity or natural gas commodity under a competitive contract.",
    explanation:
      "Suppliers compete on price and contract structure. The energy is still delivered by your utility, so switching suppliers does not affect reliability or require any physical changes at your facility.",
    whyItMatters:
      "Choosing the right supplier and contract structure is where competitive procurement creates value — and where an independent advisor helps you compare objectively.",
    related: { articles: ["utility-vs-energy-supplier", "commercial-energy-contracts-explained"], services: ["commercial-electricity", "commercial-natural-gas"] },
  },
  {
    slug: "load-factor",
    term: "Load Factor",
    group: "Electricity",
    short: "A ratio showing how steadily you use electricity relative to your peak demand.",
    definition:
      "Load factor is the ratio of your average demand to your peak demand over a period, expressed as a percentage.",
    explanation:
      "A high load factor means steady, predictable usage; a low load factor means spiky usage with high peaks relative to average consumption. Suppliers price steady, high-load-factor accounts more favorably because they are cheaper and less risky to serve.",
    whyItMatters:
      "Improving load factor — by flattening peaks or shifting load — can improve the pricing you're offered and reduce demand-related charges.",
    related: { articles: ["understanding-load-factor", "understanding-demand-charges"], services: ["commercial-electricity"] },
  },
  {
    slug: "peak-demand",
    term: "Peak Demand",
    group: "Electricity",
    short: "The single highest interval of power draw recorded during a period.",
    definition:
      "Peak demand is the maximum rate of electricity consumption (kW) recorded in any measured interval during the billing period.",
    explanation:
      "Utilities typically bill demand charges on your peak interval, and grid operators use system-wide peaks to allocate capacity and transmission costs. A brief spike can define your peak for an entire month or year.",
    whyItMatters:
      "Because a short peak can carry outsized cost, identifying and managing peak events is one of the highest-leverage actions a facility can take.",
    related: { articles: ["understanding-demand-charges", "understanding-capacity-charges"], services: ["commercial-electricity"] },
  },
  {
    slug: "kilowatt",
    term: "Kilowatt (kW)",
    group: "Units",
    short: "A unit of power equal to 1,000 watts — a measure of rate, not total energy.",
    definition:
      "A kilowatt (kW) is a unit of power representing the rate of energy use or generation at a moment in time.",
    explanation:
      "Demand is measured in kW. Think of kW as the speedometer of electricity — how fast energy is flowing — while kilowatt-hours measure the distance traveled (total energy).",
    whyItMatters:
      "Demand charges are billed per kW, so understanding this unit is essential to reading your bill and managing costs.",
    related: { articles: ["understanding-demand-charges", "understanding-commercial-utility-bills"], services: ["commercial-electricity"] },
  },
  {
    slug: "kilowatt-hour",
    term: "Kilowatt-Hour (kWh)",
    group: "Units",
    short: "The standard unit of electrical energy — using 1 kW for one hour.",
    definition:
      "A kilowatt-hour (kWh) is the amount of energy consumed by a 1 kW load operating for one hour.",
    explanation:
      "kWh measures total energy consumption over time and is the basis for the energy (commodity) portion of your bill. Ten 100-watt bulbs running for one hour use 1 kWh.",
    whyItMatters:
      "The commodity price you procure is usually quoted per kWh, making it the number most directly affected by your supply contract.",
    related: { articles: ["commercial-electricity-procurement-explained", "understanding-commercial-utility-bills"], services: ["commercial-electricity"] },
  },
  {
    slug: "megawatt",
    term: "Megawatt (MW)",
    group: "Units",
    short: "A unit of power equal to 1,000 kilowatts, used for large loads and generation.",
    definition:
      "A megawatt (MW) equals 1,000 kilowatts and is commonly used to describe large commercial/industrial loads, power plants, and battery projects.",
    explanation:
      "Wholesale electricity is priced per megawatt-hour (MWh). Large facilities and grid-scale batteries are typically sized in MW, and one MW can serve hundreds of average homes.",
    whyItMatters:
      "Understanding the MW/MWh scale helps large accounts and battery-hosting landowners interpret wholesale pricing and project sizing.",
    related: { articles: ["understanding-commercial-energy-markets", "commercial-battery-hosting-explained"], services: ["commercial-electricity", "battery-hosting"] },
  },
  {
    slug: "therm",
    term: "Therm",
    group: "Natural Gas",
    short: "A unit of natural gas energy equal to 100,000 BTU, used for commercial gas billing.",
    definition:
      "A therm is a unit of heat energy equal to 100,000 British Thermal Units (BTU), the standard unit for measuring commercial natural gas usage.",
    explanation:
      "Natural gas contracts and qualification thresholds are frequently expressed in annual therms (for example, a 50,000-therm minimum). One therm is roughly the energy in about 100 cubic feet of natural gas.",
    whyItMatters:
      "Many gas procurement programs have therm-based eligibility requirements, so knowing your annual therms determines which options are available to you.",
    related: { articles: ["commercial-natural-gas-procurement-explained", "commercial-natural-gas-buying-strategies"], services: ["commercial-natural-gas"] },
  },
  {
    slug: "commodity-charge",
    term: "Commodity Charge",
    group: "Pricing",
    short: "The cost of the actual energy you consume — the competitive part of your bill.",
    definition:
      "The commodity charge is the price for the electricity or natural gas itself, separate from the cost of delivering it.",
    explanation:
      "In deregulated markets, the commodity is what you procure competitively from a supplier. It's usually quoted per kWh (electricity) or per therm/dekatherm (gas) and is the primary lever in a procurement strategy.",
    whyItMatters:
      "The commodity charge is the portion of your bill you can actively shop and structure — making it the focus of procurement.",
    related: { articles: ["fixed-vs-indexed-pricing", "commercial-electricity-procurement-explained"], services: ["commercial-electricity", "commercial-natural-gas"] },
  },
  {
    slug: "delivery-charge",
    term: "Delivery Charge",
    group: "Pricing",
    short: "The regulated cost of transporting energy to your facility.",
    definition:
      "The delivery (or distribution) charge covers moving energy over the utility's transmission and distribution network to your meter.",
    explanation:
      "Delivery charges are set by regulators and billed by your utility regardless of supplier. They include distribution, and often transmission, taxes, and fees.",
    whyItMatters:
      "Because delivery is non-competitive, separating it from the commodity charge clarifies exactly what a procurement strategy can and cannot change.",
    related: { articles: ["understanding-commercial-utility-bills", "utility-vs-energy-supplier"], services: ["commercial-electricity", "commercial-natural-gas"] },
  },
  {
    slug: "fixed-pricing",
    term: "Fixed Pricing",
    group: "Pricing",
    short: "A contract that locks your commodity rate for the full term.",
    definition:
      "Fixed pricing locks a set price per unit of energy for the duration of the contract, providing budget certainty.",
    explanation:
      "With a fixed contract, your commodity rate does not change even if the market moves. It removes price risk from your planning but may cost slightly more than the market in exchange for that certainty.",
    whyItMatters:
      "Fixed pricing suits organizations that prioritize predictable budgets and want to eliminate exposure to market swings.",
    related: { articles: ["fixed-vs-indexed-pricing", "commercial-energy-contracts-explained"], services: ["commercial-electricity", "commercial-natural-gas"] },
  },
  {
    slug: "indexed-pricing",
    term: "Indexed Pricing",
    group: "Pricing",
    short: "A contract where your rate floats with a wholesale market index.",
    definition:
      "Indexed (or variable) pricing ties your commodity rate to a published wholesale market index, so it rises and falls with the market.",
    explanation:
      "Indexed contracts can capture savings when prices fall but expose you to volatility when they rise. They suit sophisticated buyers with the appetite and processes to manage risk actively.",
    whyItMatters:
      "Choosing between fixed and indexed structures is one of the most consequential procurement decisions — it defines your risk posture.",
    related: { articles: ["fixed-vs-indexed-pricing", "understanding-commercial-energy-markets"], services: ["commercial-electricity", "commercial-natural-gas"] },
  },
  {
    slug: "hybrid-pricing",
    term: "Hybrid Pricing",
    group: "Pricing",
    short: "A blended structure that fixes part of your load and floats the rest.",
    definition:
      "Hybrid (or blended) pricing combines fixed and indexed elements, fixing a portion of your volume while leaving some exposed to the market.",
    explanation:
      "Layered strategies let buyers balance certainty and opportunity — for example, fixing 70% of expected load for budget stability while keeping 30% indexed to benefit if prices fall.",
    whyItMatters:
      "Hybrid structures give commercial buyers a middle path, tailoring risk to their specific tolerance rather than choosing all-or-nothing.",
    related: { articles: ["fixed-vs-indexed-pricing", "commercial-electricity-buying-strategies"], services: ["commercial-electricity", "commercial-natural-gas"] },
  },
  {
    slug: "energy-deregulation",
    term: "Energy Deregulation",
    group: "Markets",
    short: "Market structure that lets customers choose their energy supplier.",
    definition:
      "Energy deregulation separates the competitive supply of energy from its regulated delivery, allowing customers to choose their supplier.",
    explanation:
      "In deregulated (competitive) markets, the utility continues to deliver energy while suppliers compete to sell the commodity. Deregulation exists for electricity and/or natural gas in a subset of U.S. states.",
    whyItMatters:
      "Deregulation is what makes commercial energy procurement possible — and why service availability varies by state.",
    related: { articles: ["what-is-energy-deregulation", "utility-vs-energy-supplier"], services: ["commercial-electricity", "commercial-natural-gas"] },
  },
  {
    slug: "renewable-energy-credits",
    term: "Renewable Energy Credits (RECs)",
    group: "Sustainability",
    short: "Tradable certificates representing the environmental attributes of renewable generation.",
    definition:
      "A Renewable Energy Credit (REC) represents proof that one megawatt-hour of electricity was generated from a renewable source.",
    explanation:
      "Purchasing RECs lets a business credibly claim renewable energy usage against its consumption, even when the physical electrons come from the general grid. RECs are a common, flexible tool for meeting sustainability goals.",
    whyItMatters:
      "RECs allow commercial organizations to green their energy profile through procurement without changing infrastructure.",
    related: { articles: ["understanding-commercial-energy-markets"], services: ["commercial-electricity"] },
  },
  {
    slug: "battery-storage",
    term: "Battery Storage",
    group: "Storage",
    short: "Systems that store electricity for later use, supporting grid reliability.",
    definition:
      "Battery energy storage systems (BESS) store electricity and discharge it when needed, providing flexibility and reliability to the grid.",
    explanation:
      "Grid-scale batteries charge when power is abundant and discharge during peaks or when renewable output drops. Their rapid expansion — especially in markets like Texas — is creating hosting opportunities for landowners.",
    whyItMatters:
      "Battery storage underpins the battery-hosting opportunity and is reshaping how modern grids balance supply and demand.",
    related: { articles: ["commercial-battery-hosting-explained"], services: ["battery-hosting"] },
  },
  {
    slug: "microgrid",
    term: "Microgrid",
    group: "Storage",
    short: "A localized energy system that can operate connected to or independent of the grid.",
    definition:
      "A microgrid is a self-contained energy system — often combining generation, storage, and controls — that can run connected to the main grid or islanded on its own.",
    explanation:
      "Microgrids improve resilience for critical facilities such as hospitals and data centers by continuing to operate during grid outages, and can optimize energy costs when grid-connected.",
    whyItMatters:
      "For facilities where reliability is mission-critical, microgrids are an increasingly relevant strategy alongside procurement.",
    related: { articles: ["understanding-commercial-energy-markets", "commercial-battery-hosting-explained"], services: ["commercial-electricity", "battery-hosting"] },
  },
];
